import { connect } from 'react-redux';
import { get } from 'lodash';
import { useHistory } from 'react-router-dom';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';

// Services
import auditService from '@services/auditLogs';

// Commons
import { Button, Dropdown } from '@commons/utils/styledLibraryComponents';
import { DATE_DISPLAY_FORMATS, TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { DEFAULT_TIMEZONE } from '@commons/utils/timezone';
import {
  GENERIC_MODAL_NO_BUTTON,
  GENERIC_MODAL_YES_BUTTON,
} from '@commons/Modals/GenericModal/genericModalActions';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import { getConfirmationModal } from '@commons/Modals/ConfirmationModal';
import { getFormattedLastUpdatedText } from '@commons/FullScreenModal/utils';
import { getTheme } from '@commons/utils/theme';
import { LoadingIcon } from '@commons/Metrics/components/Stats/styledComponents';
import { SingleDatePicker } from '@commons/DatePickers/SingleDatePicker';
import {
  TRANSFER_DETAILS_VIEW,
  TRANSFER_STATUS,
} from 'src/routes/stocks/StocksInventories/Transfers/utils/constants.js';
import Drawer from '@commons/Drawer';
import HistoryDrawerContent from '@commons/HistoryDrawer/HistoryDrawerContent';
import SpinnerIcon from '@assets/icons/new-loader.svg';
import TimePicker from '@commons/TimePicker';
import transferHeaderUtils from '@stocks/StocksInventories/Transfers/components/Header/utils';

// Selectors
import { getAuthorizedActions, getHasTransferDeliveryTime } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';
import { getSalesPointStores } from '@selectors/stores';

// Actions
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import transfersService from '@services/transfers';

// Components
import {
  formatSPAndRecipesPayloadForCreation,
  formatSPAndRecipesPayloadForDeliveryNote,
} from '../../utils/formatPayload';
import {
  HeaderContainer,
  HeaderInfosContainer,
  LeftSideContainer,
  RightSideContainer,
  Subtitle,
  Title,
  DropdownContainer,
} from './styledComponents';

export const TransfersHeader = (props) => {
  const {
    isSender,
    datePickerState,
    transferList,
    transferDetailsView,
    updateTransferList,
    openSendModal,
    allStoresOfClient,
    isFormDirty,
    recipesToDisplay,
    supplierProductsToDisplay,
    enableDatePicker = false,
    enableTimePicker = false,
    // From Redux
    client: { storeName },
    stores,
    openGenericModal,
    showSuccessMessage,
    showErrorMessage,
    pageLoading,
    pageLoaded,
    authorizedActions,
    hasTransferDeliveryTime,
  } = props;

  const history = useHistory();

  const translatedStoreNameSingular = getClientStoreNameTranslation(storeName);

  const [originalSenderStoreList, setOriginalSenderStoreList] = useState([]);
  const [originalRecipientStoreList, setOriginalRecipientStoreList] = useState([]);
  const [senderStoreList, setSenderStoreList] = useState([]);
  const [recipientStoreList, setRecipientStoreList] = useState([]);

  const [selectedSenderStore, setSelectedSenderStore] = useState(null);
  const [selectedRecipientStore, setSelectedRecipientStore] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [displayTransferHistory, setDisplayTransferHistory] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);

  // Now is used to get the hour in the reference, we need to use a state to keep a constant value across renders
  const [now] = useState(moment());

  const supplierProductsAndRecipesEntries = useMemo(
    () =>
      transferList?.status >= TRANSFER_STATUS.REQUESTED
        ? formatSPAndRecipesPayloadForDeliveryNote(supplierProductsToDisplay, recipesToDisplay)
        : formatSPAndRecipesPayloadForCreation(supplierProductsToDisplay, recipesToDisplay),
    [supplierProductsToDisplay, recipesToDisplay],
  );

  const theme = getTheme();

  /**
   * Configures and sets up the stores to display in the transfer header
   * Handles store list initialization for transfers by excluding kitchens and keep only active stores
   *
   * Then it handles two distinct scenarios:
   *
   * For new transfers (!transferList.id):
   * - Sets sender store list based on user accessible stores:
   *   • If sender: limited to user's accessible stores
   *   • If recipient: shows all active non-kitchen stores
   * - Sets recipient store list based on user accessible stores:
   *   • If recipient: limited to user's accessible stores
   *   • If sender: shows all active non-kitchen stores
   * - Auto-selects store if user has access to only one
   * - Updates transfer object with selected store IDs
   *
   * For draft transfers (transferList.id && status === 0):
   * - Retrieves existing sender/recipient store selections
   * - Rebuilds store lists considering:
   *   • Which stores user can access
   *   • Previously selected stores (even if deactivated)
   *   • Original sender/recipient relationships
   * - Maintains store selections and accessible stores
   *
   * State updates:
   * - originalSenderStoreList/originalRecipientStoreList
   * - senderStoreList/recipientStoreList
   * - selectedSenderStore/selectedRecipientStore
   * - transferList store IDs
   *
   * @async
   * @function setupStoresToDisplay
   * @throws {Error} If store fetching fails
   * @returns {Promise<void>}
   */
  const setupStoresToDisplay = () => {
    const salesPointClientStores = allStoresOfClient.filter((store) => !store.isKitchen);

    if (!transferList.id) {
      setupStoresForCreation(salesPointClientStores);
    } else if (transferList.id && transferList.status === TRANSFER_STATUS.DRAFT) {
      setupStoresForDraftDetails(salesPointClientStores);
    } else {
      setupStoresForDetails(salesPointClientStores);
    }
  };

  const setupStoresForCreation = (salesPointClientStores) => {
    const activeSalesPointClientStores = salesPointClientStores.filter(({ active }) => active);

    const currentSenderStoreList = isSender ? stores : activeSalesPointClientStores;
    const currentRecipientStoreList = !isSender ? stores : activeSalesPointClientStores;

    setOriginalSenderStoreList(currentSenderStoreList);
    setOriginalRecipientStoreList(currentRecipientStoreList);

    const senderStore = isSender && stores.length === 1 ? stores[0] : null;
    const recipientStore = !isSender && stores.length === 1 ? stores[0] : null;

    setSelectedSenderStore(senderStore);
    setSelectedRecipientStore(recipientStore);

    updateTransferList({
      senderStoreId: senderStore?.id,
      recipientStoreId: recipientStore?.id,
    });

    setSenderStoreList(currentSenderStoreList.filter((store) => store.id !== recipientStore?.id));
    setRecipientStoreList(
      currentRecipientStoreList.filter((store) => store.id !== senderStore?.id),
    );
  };

  const setupStoresForDraftDetails = (salesPointClientStores) => {
    const userStoreIdsSet = new Set(stores.map(({ id }) => id));

    // Iterating over salesPointClientStores because we want the deactivated stores if the senderStoreId or recipientStoreId is associated to a deactivated store
    const {
      senderStore,
      currentOriginalSenderStoreList,
      recipientStore,
      currentOriginalRecipientStoreList,
    } = salesPointClientStores.reduce(
      (acc, store) => {
        if (store.id === transferList.senderStoreId) {
          acc.senderStore = store;

          // If the user has access to the store, add it to the recipient store list
          if (userStoreIdsSet.has(store.id)) {
            acc.currentOriginalRecipientStoreList.push(store);
          }

          acc.currentOriginalSenderStoreList.push(store);
          return acc;
        }

        if (store.id === transferList.recipientStoreId) {
          acc.recipientStore = store;

          // If the user has access to the store, add it to the sender store list
          if (userStoreIdsSet.has(store.id)) {
            acc.currentOriginalSenderStoreList.push(store);
          }

          acc.currentOriginalRecipientStoreList.push(store);
          return acc;
        }

        // If the store is active, add it to the sender and recipient store lists depending on the isSender flag and the user stores associations
        if (store.active) {
          if (isSender) {
            if (userStoreIdsSet.has(store.id)) {
              acc.currentOriginalSenderStoreList.push(store);
            }

            acc.currentOriginalRecipientStoreList.push(store);

            return acc;
          }

          acc.currentOriginalSenderStoreList.push(store);

          if (userStoreIdsSet.has(store.id)) {
            acc.currentOriginalRecipientStoreList.push(store);
          }
        }

        return acc;
      },
      {
        senderStore: null,
        recipientStore: null,
        currentOriginalSenderStoreList: [],
        currentOriginalRecipientStoreList: [],
      },
    );

    const formattedCurrentSenderStoreList = currentOriginalSenderStoreList.filter(
      ({ id }) => id !== transferList.recipientStoreId,
    );

    const formattedCurrentRecipientStoreList = currentOriginalRecipientStoreList.filter(
      ({ id }) => id !== transferList.senderStoreId,
    );

    setSelectedSenderStore(senderStore);
    setSelectedRecipientStore(recipientStore);
    setSenderStoreList(formattedCurrentSenderStoreList);
    setRecipientStoreList(formattedCurrentRecipientStoreList);
    setOriginalSenderStoreList(currentOriginalSenderStoreList);
    setOriginalRecipientStoreList(currentOriginalRecipientStoreList);
  };

  const setupStoresForDetails = (salesPointClientStores) => {
    const recipientStore = salesPointClientStores.find(
      ({ id }) => id === transferList.recipientStoreId,
    );
    const senderStore = salesPointClientStores.find(({ id }) => id === transferList.senderStoreId);

    setSelectedSenderStore(senderStore);
    setSelectedRecipientStore(recipientStore);
  };

  const selectSenderStore = (selectedStore) => {
    updateTransferList({
      senderStoreId: selectedStore.id,
    });

    setSelectedSenderStore(selectedStore);

    const currentRecipientStoreList =
      isSender || transferList.id ? originalRecipientStoreList : stores;

    setRecipientStoreList(
      currentRecipientStoreList.filter((store) => store.id !== selectedStore.id),
    );
  };

  const selectRecipientStore = (selectedStore) => {
    updateTransferList({
      recipientStoreId: selectedStore.id,
    });

    setSelectedRecipientStore(selectedStore);

    const currentSenderStoreList = isSender && !transferList.id ? stores : originalSenderStoreList;

    setSenderStoreList(currentSenderStoreList.filter((store) => store.id !== selectedStore.id));
  };

  const generateReference = () => {
    let reference = 'TRA-';

    if (selectedSenderStore) {
      reference = reference.concat('', selectedSenderStore.partnerId);
    }

    if (selectedRecipientStore) {
      reference = reference.concat('-', selectedRecipientStore.partnerId);
    }

    if (datePickerState.date) {
      reference = reference.concat(
        '-',
        `${datePickerState.date.format(
          DATE_DISPLAY_FORMATS.SHORT_STICKED_DAY_MONTH_YEAR,
        )}-${now.format(TIME_DISPLAY_FORMATS.COMPACT_HOUR_MINUTE_SECOND)}`,
      );
    }

    return reference;
  };

  const openSenderStoreChangeWarningModal = (selectedStore) => {
    if (!isFormDirty) {
      selectSenderStore(selectedStore);
      return;
    }

    const warningModalConfig = getConfirmationModal({
      title: i18next.t('STOCKS.TRANSFERS.SENDER_STORE_CHANGE_WARNING_MODAL_TITLE'),
      content: i18next.t('STOCKS.TRANSFERS.WARNING_MODAL_CONTENT'),
      actions: [
        GENERIC_MODAL_NO_BUTTON(),
        {
          ...GENERIC_MODAL_YES_BUTTON(),
          handleClick: () => selectSenderStore(selectedStore),
        },
      ],
    });

    openGenericModal(warningModalConfig);
  };

  const openLeaveWarningModal = () => {
    const warningModalConfig = getConfirmationModal({
      title: i18next.t('GENERAL.LEAVE'),
      content: i18next.t('STOCKS.TRANSFERS.WARNING_MODAL_CONTENT'),
      actions: [
        GENERIC_MODAL_NO_BUTTON(),
        {
          ...GENERIC_MODAL_YES_BUTTON(),
          handleClick: () => history.push('/stocks/inventories/transfers'),
        },
      ],
    });

    openGenericModal(warningModalConfig);
  };

  const handleClose = () => {
    transferList.status > TRANSFER_STATUS.SENT
      ? history.push('/stocks/inventories/transfers')
      : openLeaveWarningModal();
  };

  const handleSave = async (isValidating = false) => {
    if (transferDetailsView === TRANSFER_DETAILS_VIEW.DISABLED) {
      return;
    }

    const transferListPayload = {
      ...transferList,
      sentAt: datePickerState.date.format(),
      isValidating,
      emailsTo: [],
      emailsCC: [],
    };

    if (transferDetailsView === TRANSFER_DETAILS_VIEW.DELIVERY) {
      await handleUpdateTransferDeliveryNote(transferListPayload);
      return;
    }

    if (transferDetailsView === TRANSFER_DETAILS_VIEW.RECEPTION) {
      const receptionPayload = {
        ...transferList,
        sentAt: datePickerState.date.format(),
        isValidating,
      };

      await handleUpdateTransferReception(receptionPayload);
      return;
    }

    await handleCreateTransfer(transferListPayload);
  };

  const handleCreateTransfer = async (transferList) => {
    const { supplierProducts, recipes } = supplierProductsAndRecipesEntries;

    try {
      pageLoading();

      await transfersService.createTransfer({
        transferList,
        supplierProducts,
        recipes,
      });

      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.CREATION_SUCCESS'));
      history.push('/stocks/inventories/transfers');
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.CREATION_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleUpdateTransferReception = async (transferList) => {
    const { supplierProducts, recipes } = supplierProductsAndRecipesEntries;

    try {
      pageLoading();

      await transfersService.updateTransferReception({
        transferList,
        supplierProducts,
        recipes,
      });

      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.EDIT_SUCCESS'));
      history.push('/stocks/inventories/transfers');
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const handleUpdateTransferDeliveryNote = async (transferList) => {
    const { supplierProducts, recipes } = supplierProductsAndRecipesEntries;

    try {
      pageLoading();

      await transfersService.updateTransferDeliveryNote({
        transferList,
        supplierProducts,
        recipes,
      });

      showSuccessMessage(i18next.t('STOCKS.TRANSFERS.EDIT_SUCCESS'));
      history.push('/stocks/inventories/transfers');
    } catch {
      showErrorMessage(i18next.t('STOCKS.TRANSFERS.EDIT_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  useEffect(() => {
    setupStoresToDisplay();
  }, [allStoresOfClient]);

  useEffect(() => {
    if (transferList.id) {
      return;
    }

    updateTransferList({
      reference: generateReference(),
    });
  }, [selectedSenderStore, selectedRecipientStore, datePickerState.date]);

  useEffect(() => {
    if (transferList && transferList.id) {
      fetchTransferListHistory(transferList.id);
    }
  }, [transferList.id]);

  const isHeaderDisabled = transferHeaderUtils.isHeaderDisabled(transferList.status, isSender);

  const isSenderStoreDropdownDisabled = transferHeaderUtils.isSenderStoreDropdownDisabled(
    senderStoreList.length,
    !!selectedSenderStore,
    isHeaderDisabled,
  );

  const isRecipientStoreDropdownDisabled = transferHeaderUtils.isRecipientStoreDropdownDisabled(
    recipientStoreList.length,
    !!selectedRecipientStore,
    isHeaderDisabled,
  );

  const preventSaveOrSend = transferHeaderUtils.shouldPreventSaveOrSend({
    selectedSenderStore,
    selectedRecipientStore,
    date: datePickerState.date,
    status: transferList.status,
    supplierProductsCount: supplierProductsAndRecipesEntries.supplierProducts.length,
    recipesCount: supplierProductsAndRecipesEntries.recipes.length,
    transferDetailsView,
    authorizedActions,
  });

  const handleOpenDrawer = () => {
    if (!transferList) {
      return;
    }

    setDisplayTransferHistory(!displayTransferHistory);
  };

  const fetchTransferListHistory = async (transferListId) => {
    setIsLoading(true);
    try {
      const transferListAuditLogs = await auditService.getTargetIdAuditLogs(transferListId);
      setAuditLogs(transferListAuditLogs);
    } catch {
      showErrorMessage(i18next.t('AUDIT_EVENTS.ERROR_FETCHING'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <HeaderContainer>
      <LeftSideContainer>
        <DropdownContainer>
          <Dropdown
            customStyle={{ position: 'inherit' }}
            iconSrc={
              !selectedSenderStore || isSenderStoreDropdownDisabled
                ? '/images/inpulse/store-dmgrey-small.svg'
                : '/images/inpulse/store-black-small.svg'
            }
            isDisabled={isSenderStoreDropdownDisabled}
            items={senderStoreList}
            placeholder={i18next.t('STOCKS.TRANSFERS.FORM_SENDER_PLACEHOLDER', {
              storeName: translatedStoreNameSingular,
            })}
            selectedItem={selectedSenderStore}
            isRequired
            isUniqueSelection
            onSelectionChange={openSenderStoreChangeWarningModal}
          />
          <img src="/images/inpulse/arrow-right-ipblack4.svg" />
          <Dropdown
            customStyle={{ position: 'inherit' }}
            iconSrc={
              !selectedRecipientStore || isRecipientStoreDropdownDisabled
                ? '/images/inpulse/store-dmgrey-small.svg'
                : '/images/inpulse/store-black-small.svg'
            }
            isDisabled={isRecipientStoreDropdownDisabled}
            items={recipientStoreList}
            placeholder={i18next.t('STOCKS.TRANSFERS.RECIPIENT_STORE', {
              storeName: translatedStoreNameSingular,
            })}
            selectedItem={selectedRecipientStore}
            isRequired
            isUniqueSelection
            onSelectionChange={selectRecipientStore}
          />
        </DropdownContainer>

        <SingleDatePicker
          customStyle={{ position: 'inherit' }}
          date={datePickerState.date}
          disabled={!enableDatePicker && isHeaderDisabled}
          placeholder={i18next.t('GENERAL.DATEPICKER_PLACEHOLDER')}
          timezone={get(
            isSender ? selectedSenderStore : selectedRecipientStore,
            'timezone',
            DEFAULT_TIMEZONE,
          )}
          onDateChange={(newDate) => {
            datePickerState.setDate(newDate);
            updateTransferList({
              sentAt: newDate.format(),
            });
          }}
        />

        {hasTransferDeliveryTime && (
          <TimePicker
            customStyle={{ position: 'inherit', width: 180 }}
            disabled={!enableTimePicker && isHeaderDisabled}
            placeholder={i18next.t('STOCKS.TRANSFERS.DELIVERY_TIME')}
            value={
              transferList.deliveryTime
                ? moment(transferList.deliveryTime, TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE)
                : null
            }
            onChange={(newDeliveryTime) => {
              if (!newDeliveryTime.isValid()) {
                return;
              }
              updateTransferList({
                deliveryTime: newDeliveryTime.format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE),
              });
            }}
          />
        )}

        <HeaderInfosContainer>
          <Title>{transferList.reference}</Title>
          {!transferList.id && <Subtitle>{getFormattedLastUpdatedText(now)}</Subtitle>}
          {isLoading && transferList.id && (
            <LoadingIcon alt={'component-loader'} height="16px" src={SpinnerIcon} width="16px" />
          )}
          {!isLoading && transferList.id && (
            <Button
              buttonCustomStyle={{
                paddingLeft: '0px',
                height: '20px',
                font: theme.fonts.textSmallHeight14,
              }}
              color={'inpulse-outline'}
              fontColor={theme.colors.blacks.ipBlack4}
              handleClick={handleOpenDrawer}
              icon={'/images/inpulse/history.svg'}
              iconOnLeft={false}
              label={getFormattedLastUpdatedText(transferList.updatedAt)}
              noBorder
            />
          )}
        </HeaderInfosContainer>
      </LeftSideContainer>
      <RightSideContainer>
        <Button
          color={'inpulse-grey'}
          handleClick={handleClose}
          icon={'/images/inpulse/close-white-small.svg'}
        />
        {transferDetailsView !== TRANSFER_DETAILS_VIEW.DISABLED &&
          transferDetailsView !== TRANSFER_DETAILS_VIEW.SENDER_DISABLED && (
            <>
              <Button
                color={'inpulse-outline'}
                handleClick={() => handleSave(false)}
                icon={'/images/inpulse/save-black-small.svg'}
                isDisabled={preventSaveOrSend}
              />
              <Button
                color={'inpulse-default'}
                handleClick={
                  transferDetailsView === TRANSFER_DETAILS_VIEW.RECEPTION
                    ? () => handleSave(true)
                    : () => openSendModal()
                }
                icon={
                  transferDetailsView === TRANSFER_DETAILS_VIEW.RECEPTION
                    ? '/images/inpulse/done-white-small.svg'
                    : '/images/inpulse/send-white-small.svg'
                }
                isDisabled={preventSaveOrSend}
              />
            </>
          )}
      </RightSideContainer>

      <Drawer
        isOpened={displayTransferHistory}
        overlayOpacity={0}
        onClick={() => {
          setDisplayTransferHistory(false);
        }}
      >
        <HistoryDrawerContent
          auditLogs={auditLogs}
          subtitle={transferList.reference}
          onClose={() => setDisplayTransferHistory(false)}
        />
      </Drawer>
    </HeaderContainer>
  );
};

const mapStateToProps = (state) => ({
  stores: getSalesPointStores(state.baseReducer.activeStores),
  client: getClientInfo(state.baseReducer.user),
  authorizedActions: getAuthorizedActions(
    state.baseReducer.userRights,
    '/stocks/inventories/transfers',
  ),
  hasTransferDeliveryTime: getHasTransferDeliveryTime(state.baseReducer.userRights),
});

const mapDispatchToProps = (dispatch) => ({
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(TransfersHeader);
